/**
 * Delivery Management Page - Simplified
 * Simple flow: See pending items → Set quantity → Mark delivered
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, formatINR, useAuth } from '../lib/store';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { logger } from '../lib/logger';
import { 
  Package, Truck, CheckCircle, Clock, Search, User, Undo2
} from 'lucide-react';

export default function DeliveryPage() {
  const navigate = useNavigate();
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  
  // Users for filtering
  const [users, setUsers] = useState({ salespeople: [], creators: [], admins: [] });
  
  // Delivery dialog
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [deliveryItems, setDeliveryItems] = useState([]);
  const [delivering, setDelivering] = useState(false);
  const [deliveryNote, setDeliveryNote] = useState('');
  
  // User info
  const [userRole, setUserRole] = useState('staff');
  // Same role permission routes/delivery.py checks to revert a delivery.
  const { can } = useAuth();
  const isAdmin = can('delivery.revert');
  const canFilterUsers = userRole === 'admin' || userRole === 'creator' || userRole === 'stock_manager';

  useEffect(() => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        setUserRole(user.role || 'staff');
      }
    } catch (e) {
      logger.error('Failed to parse user data:', e);
    }
  }, []);

  // Fetch quotations
  const fetchQuotations = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (selectedUserId) params.user_id = selectedUserId;
      if (searchQuery) params.search = searchQuery;
      
      const res = await api.getDeliveryQuotations(params);
      setQuotations(res.data.quotations || []);
    } catch (err) {
      toast.error('Failed to load quotations');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, selectedUserId, searchQuery]);

  // Fetch users for filter
  const fetchUsers = useCallback(async () => {
    if (!canFilterUsers) return;
    try {
      const res = await api.getDeliveryUsers();
      setUsers(res.data || { salespeople: [], creators: [], admins: [] });
    } catch (err) {
      logger.error('Failed to load users');
    }
  }, [canFilterUsers]);

  // Debounced fetch — covers filter changes and search typing alike.
  useEffect(() => {
    const timer = setTimeout(fetchQuotations, 300);
    return () => clearTimeout(timer);
  }, [fetchQuotations]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Open delivery dialog
  const openDeliveryDialog = async (quotation) => {
    setSelectedQuotation(quotation);
    setDeliveryNote('');
    
    try {
      const res = await api.getDeliveryDetails(quotation.id);
      const items = (res.data.items || []).map(item => ({
        ...item,
        deliverQty: item.pending_qty, // Pre-fill with pending quantity
        selected: item.pending_qty > 0 // Auto-select items with pending qty
      }));
      setDeliveryItems(items);
    } catch (err) {
      toast.error('Failed to load items');
      setSelectedQuotation(null);
    }
  };

  // Toggle item selection
  const toggleItemSelection = (index) => {
    setDeliveryItems(prev => prev.map((item, i) => 
      i === index ? { ...item, selected: !item.selected } : item
    ));
  };

  // Update delivery quantity
  const updateDeliveryQty = (index, qty) => {
    const maxQty = deliveryItems[index].pending_qty;
    const newQty = Math.min(Math.max(0, qty), maxQty);
    setDeliveryItems(prev => prev.map((item, i) => 
      i === index ? { ...item, deliverQty: newQty, selected: newQty > 0 } : item
    ));
  };

  // Deliver selected items
  const handleDeliver = async () => {
    const itemsToDeliver = deliveryItems
      .filter(item => item.selected && item.deliverQty > 0)
      .map(item => ({
        item_index: item.item_index,
        deliver_qty: item.deliverQty
      }));

    if (itemsToDeliver.length === 0) {
      toast.error('Please select items to deliver');
      return;
    }

    setDelivering(true);
    try {
      await api.deliverItems(selectedQuotation.id, {
        items: itemsToDeliver,
        delivery_note: deliveryNote
      });
      toast.success('Delivery marked successfully!');
      setSelectedQuotation(null);
      fetchQuotations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark delivery');
    } finally {
      setDelivering(false);
    }
  };

  // Deliver all items at once
  const handleDeliverAll = async () => {
    const itemsToDeliver = deliveryItems
      .filter(item => item.pending_qty > 0)
      .map(item => ({
        item_index: item.item_index,
        deliver_qty: item.pending_qty
      }));

    if (itemsToDeliver.length === 0) {
      toast.error('No pending items to deliver');
      return;
    }

    setDelivering(true);
    try {
      await api.deliverItems(selectedQuotation.id, {
        items: itemsToDeliver,
        delivery_note: deliveryNote
      });
      toast.success('All items delivered!');
      setSelectedQuotation(null);
      fetchQuotations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark delivery');
    } finally {
      setDelivering(false);
    }
  };

  // Revert delivery for an item
  const handleRevert = async (item) => {
    if (!isAdmin) return;
    if (!window.confirm(`Revert delivery for ${item.description}?`)) return;
    
    try {
      await api.revertDelivery(selectedQuotation.id, {
        item_index: item.item_index
      });
      toast.success('Delivery reverted');
      // Refresh the dialog
      openDeliveryDialog(selectedQuotation);
      fetchQuotations();
    } catch (err) {
      toast.error('Failed to revert');
    }
  };

  // Stats
  const stats = useMemo(() => {
    const pending = quotations.filter(q => q.delivery_status === 'pending').length;
    const partial = quotations.filter(q => q.delivery_status === 'partial').length;
    const delivered = quotations.filter(q => q.delivery_status === 'delivered').length;
    return { pending, partial, delivered };
  }, [quotations]);

  // Filter quotations
  const filteredQuotations = quotations;

  return (
    <div className="pb-6" data-testid="delivery-page">
      {/* Header */}
      <div className="heda-card mb-4">
        <div className="p-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-heda-olive-tint flex items-center justify-center">
              <Truck className="w-5 h-5 text-heda-olive-dark" />
            </div>
            <div>
              <h1 className="font-display text-[20px] font-bold text-heda-ink">Deliveries</h1>
              <p className="text-xs text-heda-mute">Mark items as delivered</p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <button 
              onClick={() => setStatusFilter(statusFilter === 'pending' ? '' : 'pending')}
              className={`p-3 rounded-xl text-center transition-all ${statusFilter === 'pending' ? 'bg-amber-50 border-2 border-amber-200' : 'bg-amber-50'}`}
            >
              <Clock className="w-4 h-4 mx-auto mb-1 text-amber-700" />
              <p className="text-lg font-bold text-amber-700">{stats.pending}</p>
              <p className="text-[10px] text-amber-700">Pending</p>
            </button>
            <button 
              onClick={() => setStatusFilter(statusFilter === 'partial' ? '' : 'partial')}
              className={`p-3 rounded-xl text-center transition-all ${statusFilter === 'partial' ? 'bg-amber-50 border-2 border-amber-200' : 'bg-amber-50'}`}
            >
              <Package className="w-4 h-4 mx-auto mb-1 text-amber-700" />
              <p className="text-lg font-bold text-amber-700">{stats.partial}</p>
              <p className="text-[10px] text-amber-700">Partial</p>
            </button>
            <button 
              onClick={() => setStatusFilter(statusFilter === 'delivered' ? '' : 'delivered')}
              className={`p-3 rounded-xl text-center transition-all ${statusFilter === 'delivered' ? 'bg-emerald-50 border-2 border-emerald-200' : 'bg-emerald-50'}`}
            >
              <CheckCircle className="w-4 h-4 mx-auto mb-1 text-emerald-700" />
              <p className="text-lg font-bold text-emerald-700">{stats.delivered}</p>
              <p className="text-[10px] text-emerald-700">Delivered</p>
            </button>
          </div>

          {/* Search & Filter */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-heda-mute" />
              <Input
                placeholder="Search customer or quote..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="rounded-xl pl-9 bg-heda-cream/50 border-heda-line text-sm h-10"
              />
            </div>
            {canFilterUsers && (
              <Select value={selectedUserId || 'all'} onValueChange={(val) => setSelectedUserId(val === 'all' ? '' : val)}>
                <SelectTrigger className="rounded-xl w-[130px] bg-heda-cream/50 border-heda-line text-sm h-10">
                  <User className="w-3 h-3 mr-1" />
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {users.salespeople?.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      </div>

      {/* Quotation List */}
      <div>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-heda-gold border-t-transparent rounded-full" />
          </div>
        ) : filteredQuotations.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-heda-olive-tint">
              <Package className="h-7 w-7 text-heda-olive" />
            </div>
            <p className="font-display font-bold text-heda-ink">No quotations found</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredQuotations.map(q => (
              <Card 
                key={q.id} 
                className="rounded-2xl bg-white shadow-none cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => openDeliveryDialog(q)}
                data-testid={`delivery-card-${q.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-mono text-sm font-bold text-heda-olive-dark">{q.quotation_number}</p>
                        <StatusBadge status={q.delivery_status} />
                      </div>
                      <p className="font-medium text-heda-ink">{q.customer?.name}</p>
                      <p className="text-xs text-heda-mute">
                        {q.items?.length} items{typeof q.total === 'number' ? ` • ${formatINR(q.total)}` : ''}
                      </p>
                    </div>
                    <Truck className="w-5 h-5 text-heda-mute" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Delivery Dialog */}
      <Dialog open={!!selectedQuotation} onOpenChange={(open) => !open && setSelectedQuotation(null)}>
        <DialogContent className="border-heda-line bg-white sm:rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto p-0" aria-describedby="delivery-dialog-desc">
          <div className="sticky top-0 bg-white border-b border-heda-line p-4 z-10">
            <DialogHeader>
              <DialogTitle className="text-base">
                {selectedQuotation?.quotation_number}
              </DialogTitle>
            </DialogHeader>
            <p id="delivery-dialog-desc" className="text-sm text-heda-mute mt-1">
              {selectedQuotation?.customer?.name}{typeof selectedQuotation?.total === 'number' ? ` • ${formatINR(selectedQuotation.total)}` : ''}
            </p>
          </div>

          <div className="p-4 space-y-3">
            {deliveryItems.map((item, idx) => (
              <DeliveryItemCard
                key={idx}
                item={item}
                onToggle={() => toggleItemSelection(idx)}
                onQtyChange={(qty) => updateDeliveryQty(idx, qty)}
                onRevert={() => handleRevert(item)}
                canRevert={isAdmin && item.delivered_qty > 0}
              />
            ))}

            {/* Note */}
            <Input
              placeholder="Delivery note (optional)"
              value={deliveryNote}
              onChange={(e) => setDeliveryNote(e.target.value)}
              className="rounded-xl bg-heda-cream/50 text-sm"
            />
          </div>

          {/* Actions */}
          <div className="sticky bottom-0 bg-white border-t border-heda-line p-4 space-y-2">
            {deliveryItems.some(i => i.pending_qty > 0) && (
              <>
                <Button
                  onClick={handleDeliverAll}
                  disabled={delivering}
                  className="rounded-xl w-full bg-heda-ink hover:bg-black"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {delivering ? 'Marking...' : 'Deliver All'}
                </Button>
                <Button
                  onClick={handleDeliver}
                  disabled={delivering || !deliveryItems.some(i => i.selected && i.deliverQty > 0)}
                  variant="outline"
                  className="rounded-xl w-full bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold"
                >
                  Deliver Selected Only
                </Button>
              </>
            )}
            {!deliveryItems.some(i => i.pending_qty > 0) && (
              <p className="text-center text-sm text-emerald-700 font-medium py-2">
                <CheckCircle className="w-4 h-4 inline mr-1" />
                All items delivered!
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Status Badge Component
function StatusBadge({ status }) {
  const config = {
    pending: { bg: 'bg-amber-50', text: 'text-amber-700', label: 'Pending' },
    partial: { bg: 'bg-amber-50', text: 'text-amber-700', label: 'Partial' },
    delivered: { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Delivered' }
  };
  const c = config[status] || config.pending;
  return <Badge className={`rounded-full ${c.bg} ${c.text} text-[10px]`}>{c.label}</Badge>;
}

// Delivery Item Card - Simplified
function DeliveryItemCard({ item, onToggle, onQtyChange, onRevert, canRevert }) {
  const isDelivered = item.pending_qty === 0;
  const isPartial = item.delivered_qty > 0 && item.pending_qty > 0;

  return (
    <div className={`rounded-xl border p-3 ${isDelivered ? 'bg-emerald-50 border-emerald-200' : isPartial ? 'bg-amber-50 border-amber-200' : 'bg-white border-heda-line'}`}>
      {/* Product Info */}
      <div className="flex items-start gap-3">
        {!isDelivered && (
          <input
            type="checkbox"
            checked={item.selected}
            onChange={onToggle}
            className="mt-1 w-5 h-5 rounded-lg border-heda-line text-heda-olive-dark focus:ring-heda-gold"
          />
        )}
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm text-heda-ink truncate">{item.description}</p>
          <p className="text-xs text-heda-mute">{item.size} • {item.thickness}</p>
          
          {/* Quantity Info */}
          <div className="flex items-center gap-4 mt-2 text-xs">
            <span className="text-heda-mute">
              Ordered: <strong>{item.quantity}</strong>
            </span>
            {item.delivered_qty > 0 && (
              <span className="text-emerald-700">
                Delivered: <strong>{item.delivered_qty}</strong>
              </span>
            )}
            {item.pending_qty > 0 && (
              <span className="text-amber-700">
                Pending: <strong>{item.pending_qty}</strong>
              </span>
            )}
          </div>
        </div>

        {/* Quantity Input or Delivered Badge */}
        {isDelivered ? (
          <div className="flex items-center gap-2">
            <Badge className="rounded-full bg-emerald-50 text-emerald-700">
              <CheckCircle className="w-3 h-3 mr-1" /> Done
            </Badge>
            {canRevert && (
              <Button variant="ghost" size="sm" onClick={onRevert} className="rounded-xl h-7 px-2 text-heda-mute">
                <Undo2 className="w-3 h-3" />
              </Button>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <Input
              type="number"
              min="0"
              max={item.pending_qty}
              value={item.deliverQty}
              onChange={(e) => onQtyChange(parseInt(e.target.value) || 0)}
              className="rounded-xl bg-heda-cream/50 w-16 h-8 text-center text-sm"
            />
            <span className="text-xs text-heda-mute">/ {item.pending_qty}</span>
          </div>
        )}
      </div>

      {/* Revert for partial */}
      {isPartial && canRevert && (
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={onRevert}
          className="rounded-xl mt-2 text-xs text-heda-mute h-6"
        >
          <Undo2 className="w-3 h-3 mr-1" /> Revert delivered
        </Button>
      )}
    </div>
  );
}
